import React, { useState, useEffect } from 'react';
import { X, Mail, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { InvitationFormData, EmailCheckResponse, User, BusinessRole } from '../types';
import { apiService } from '../services/api';

interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteSent: () => void;
  businessId: string;
}

const InvitationModal: React.FC<InvitationModalProps> = ({
  isOpen,
  onClose,
  onInviteSent,
  businessId
}) => {
  const [formData, setFormData] = useState<InvitationFormData>({
    email: '',
    roleIds: []
  });
  const [roles, setRoles] = useState<BusinessRole[]>([]);
  const [emailCheck, setEmailCheck] = useState<EmailCheckResponse | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadRoles();
      loadCurrentUser();
    }
  }, [isOpen, businessId]);

  const loadCurrentUser = async () => {
    try {
      const user = await apiService.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadRoles = async () => {
    if (!businessId) {
      setError('Business information not loaded. Please try again.');
      return;
    }
    
    try {
      const rolesData = await apiService.getBusinessRoles(businessId);
      // Transform business roles to match BusinessRole interface for display
      const transformedRoles = rolesData
        .filter(role => role.is_active)
        .map((role: any) => ({
          id: role.id,
          businessId: role.business_id,
          name: role.name,
          description: role.description,
          permissions: role.permissions?.map((p: any) => p.name) || [],
          isActive: role.is_active,
          dashboardType: role.dashboard_type,
          createdAt: role.created_at,
          updatedAt: role.updated_at
        }));
      setRoles(transformedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load business roles');
    }
  };

  const handleEmailChange = async (email: string) => {
    const lowerCaseEmail = email.toLowerCase();
    setFormData(prev => ({ ...prev, email: lowerCaseEmail }));
    setEmailCheck(null);
    setError('');

    if (lowerCaseEmail && lowerCaseEmail.includes('@')) {
      setIsCheckingEmail(true);
      try {
        const checkResult = await apiService.checkEmailExists(lowerCaseEmail);
        setEmailCheck(checkResult);
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setIsCheckingEmail(false);
      }
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds // If clicking on the currently selected role, don't allow deselection
        : [roleId] // Replace the current selection with the new role (ensuring exactly 1 role)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Please enter an email address');
      return;
    }

    if (formData.roleIds.length !== 1) {
      setError('Please select exactly one role for this personnel');
      return;
    }

    if (!businessId) {
      setError('Business information not loaded. Please try again.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const invitationData = {
        ...formData,
        inviterName: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'The business owner'
      };
      
      await apiService.createInvitation(businessId, invitationData);
      onInviteSent();
      onClose();
      setFormData({ email: '', roleIds: [] });
      setEmailCheck(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Invite Personnel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter email address"
                required
              />
            </div>
            
            {/* Email Status */}
            {isCheckingEmail && (
              <div className="mt-2 flex items-center text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Checking email...
              </div>
            )}
            
            {emailCheck && !isCheckingEmail && (
              <div className={`mt-2 flex items-center text-sm ${
                emailCheck.exists ? 'text-green-600' : 'text-blue-600'
              }`}>
                {emailCheck.exists ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span>Account exists for {emailCheck.user?.firstName} {emailCheck.user?.lastName}</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <span>New account will be created</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Assign Role
            </label>
            <div className="space-y-3">
              {roles.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <Shield className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No business roles available</p>
                  <p className="text-sm">Create roles first in the Roles section</p>
                </div>
              ) : (
                roles.map((role) => (
                  <label key={role.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.roleIds.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {role.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {role.description}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center text-red-600">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 aacore-btn2-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.email || formData.roleIds.length !== 1}
              className="flex-1 aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvitationModal; 